import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { AuthService } from '../auth';
import { environment } from '../environments/environment';
import { switchMap, catchError } from 'rxjs/operators';
import { Inject, Injectable } from '@angular/core';
import { logAuthError, logAuthWarn } from '../auth/auth-debug.helpers';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(@Inject(AuthService) private authService: AuthService) {}

  private readonly excludes = ['oauth/token', 'storage'];

  private isExcluded(req: HttpRequest<any>): boolean {
    if (this.excludes.some(e => req.url.includes(e))) {
      return true;
    }
    // Presigned S3 (and other absolute non-API) URLs must not get Bearer:
    // S3 returns 400 "Only one auth mechanism allowed".
    return this.isExternalOrPresignedUrl(req.url);
  }

  private isExternalOrPresignedUrl(url: string): boolean {
    if (!url) {
      return false;
    }
    if (
      url.includes('X-Amz-Signature') ||
      url.includes('amazonaws.com') ||
      url.includes('.s3.')
    ) {
      return true;
    }
    if (!/^https?:\/\//i.test(url)) {
      return false;
    }
    const bases = [environment.api?.base, environment.api?.initBase].filter(
      (b): b is string => !!b
    );
    return bases.length > 0 && !bases.some(base => url.startsWith(base));
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return this.isExcluded(req)
      ? next.handle(req)
      : from(this.authService.token()).pipe(
          switchMap(token => {
            let outgoing = req;
            // Only attach a valid bearer. A stale Authorization header makes
            // Passport reject public catalog (GET /tags, /forms) with 401 and
            // breaks the anonymous map even when reports are published.
            if (token && this.authService.isTokenValid(token)) {
              outgoing = req.clone({
                headers: req.headers.set(
                  environment.authentication.token_header,
                  `${token.token_type} ${token.access_token}`
                )
              });
            } else if (token) {
              logAuthWarn('auth.interceptor: token present but invalid', {
                url: req.url
              });
              this.authService.clearToken('auth.interceptor: token present but invalid');
            }
            return next.handle(outgoing);
          }),
          catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              logAuthError('auth.interceptor: 401 on token resolution', error, {
                url: req.url
              });
              this.authService.clearToken('auth.interceptor: 401 on token resolution');
            }
            return throwError(() => error);
          })
        );
  }
}
